import React, { useState, useEffect } from 'react';
import { useWorkspacesContext } from '../../contexts/WorkspacesContext'; 
import api from '../../api/axios';
import { Loader2, Link } from 'lucide-react';

export default function AddAccountModal ({ show, onClose })  {
    
    const { addWorkspace } = useWorkspacesContext();
    
    const [formData, setFormData] = useState({
        name: '',    
        email: '',  
        token: '', 
        createdAt: new Date().toISOString(),
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); 
    const [isConnected, setIsConnected] = useState(false);
    const [connectionLoading, setConnectionLoading] = useState(false);

    useEffect(() => {
        setIsConnected(false);
    }, [formData.email, formData.token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckConnection = async () => {
        if (!formData.email || !formData.token) {
            setError('Please provide email and app password first.');
            return;
        }

        setConnectionLoading(true);
        setError('');
        try {
            const body = {
                username: formData.email,
                appPassword: formData.token,
                folder: 'INBOX',
                sinceDays: 0,
                startIndex: 0,
                endIndex: 1,
            };
            const res = await api.post("/mail/search", body);
            if (Array.isArray(res.data)) {
                setIsConnected(true);
                alert("Connection Successful!");
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (err) {
            console.error("Connection check failed:", err);
            setError("Connection failed. Check your email and app password.");
            setIsConnected(false);
        } finally {
            setConnectionLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.name || !formData.email || !formData.token) {
            setError('Please fill all required fields.');
            return;
        }

        if (!isConnected) {
            setError('Please check connection first.');
            return;
        }

        setLoading(true);

        try {
            await addWorkspace(formData.email, formData.token, formData.name);

            setFormData({ name: '', email: '', token: '', createdAt: new Date().toISOString() });
            setIsConnected(false);
            onClose(); 
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div 
            className="modal d-block fade show" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} 
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">Create New Workspace (Gmail)</h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            {error && <div className="alert alert-danger text-center">{error}</div>}
                            
                            <div className="mb-3">
                                <label htmlFor="name" className="form-label">Tab Name</label>
                                <input
                                    type="text" className="form-control" id="name" name="name"
                                    value={formData.name} onChange={handleChange}
                                    placeholder="Business Account 1" required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="email" className="form-label">Gmail Address</label>
                                <input
                                    type="email" className="form-control" id="email" name="email"
                                    value={formData.email} onChange={handleChange}
                                    placeholder="example@gmail.com" required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="token" className="form-label">App Password</label>
                                <input
                                    type="password" className="form-control" id="token" name="token"
                                    value={formData.token} onChange={handleChange}
                                    placeholder="Enter App Password" required
                                />
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button 
                                type="button" 
                                className={`btn ${isConnected ? 'btn-success' : 'btn-info'} d-flex align-items-center gap-2`}
                                onClick={handleCheckConnection}
                                disabled={connectionLoading || !formData.email || !formData.token}
                            >
                                {connectionLoading ? <Loader2 size={18} className="spin-animation" /> : <Link size={18} />}
                                {isConnected ? 'Connection Verified' : 'Check Connection'}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading || !isConnected}>
                                {loading ? 'Saving...' : 'Save & Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};